/**
 * The win-rate curve, read-side.
 *
 * A faithful port of the backend's read-time weighting so the rep workspace
 * shows the same £ the projection computes. The three functions here mirror,
 * in order:
 *
 * - ``interp_curve``            (sales_forecasting_module/models/bucket_calibration.py)
 * - the ``remaining`` step      (actions/compute_forecast/compute.py:595)
 * - ``project_curve``           (actions/compute_forecast/compute.py:557)
 *
 * The mechanism in one line: an open quote of working-day-age A carries
 * remaining expected conversion ``plateau - C(A)``, and that remainder is
 * phased over *future* booking dates by the curve's own forward increments.
 * Past the tail the remainder is 0 — an implicit, per-bucket write-off with no
 * write-off constant, which is why an aged quote silently contributes nothing.
 *
 * Two invariants carried over from the backend, both easy to "helpfully" break:
 *  - The plateau is NOT bounded by 1.0 and must never be clamped. Its numerator
 *    is ordered £ and its denominator quoted £, and orders legitimately land
 *    above quote.
 *  - C(age) is an absolute share of the decided cohort, not a fraction of the
 *    plateau, because both share that denominator.
 */

/** One anchor of a cumulative win-rate curve: [working-day age, C(age)]. */
export type CurvePoint = [ageWd: number, cumulative: number];

/**
 * The anchor ages every curve is sampled at. Mirrors
 * ``WIN_RATE_CURVE_ANCHOR_DAYS``; the projection phases conversion onto exactly
 * these offsets, so a UI that invents its own anchors will not reconcile.
 */
export const WIN_RATE_CURVE_ANCHOR_DAYS: readonly number[] = [
  0, 1, 2, 3, 5, 7, 14, 21, 30, 60, 90, 180, 365,
];

/**
 * Cumulative win-rate C(age), linearly interpolated between anchors.
 * Flat before the first anchor (its value) and after the last (the plateau).
 */
export function interpCurve(curve: readonly CurvePoint[], age: number): number {
  if (curve.length === 0) return 0;
  if (age <= curve[0][0]) return curve[0][1];
  if (age >= curve[curve.length - 1][0]) return curve[curve.length - 1][1];
  for (let i = 0; i < curve.length - 1; i++) {
    const [d0, c0] = curve[i];
    const [d1, c1] = curve[i + 1];
    if (d0 <= age && age <= d1) {
      if (d1 === d0) return c1;
      return c0 + ((age - d0) / (d1 - d0)) * (c1 - c0);
    }
  }
  return curve[curve.length - 1][1];
}

/** The value the curve flattens to — the bucket's value-weighted win rate. */
export function plateauOf(curve: readonly CurvePoint[]): number {
  return curve.length ? curve[curve.length - 1][1] : 0;
}

/**
 * Remaining expected conversion for an open quote of the given working-day age:
 * ``plateau - C(age)``, floored at 0.
 *
 * Zero means written off — the quote has outlived the curve and contributes
 * nothing to the projection. That is the number the hygiene queue exists to
 * surface, so callers should distinguish it from "small", not round it away.
 */
export function remainingConversion(
  curve: readonly CurvePoint[],
  ageWd: number,
): number {
  return Math.max(0, plateauOf(curve) - interpCurve(curve, ageWd));
}

/** One future landing of a quote's remaining conversion. */
export interface CurveLanding {
  /** Working-day offset from the estimate date — a curve anchor beyond `ageWd`. */
  anchorWd: number;
  /** Share of the quote's quoted value landing at this anchor. */
  share: number;
}

/**
 * Phase a quote's remaining conversion over future booking dates by the curve's
 * forward increments ``dC``. Only anchors strictly beyond the quote's current
 * age contribute, which is why an open quote never lands in the past.
 *
 * The shares sum to ``remainingConversion(curve, ageWd)``, not to 1.
 */
export function curveLandings(
  curve: readonly CurvePoint[],
  ageWd: number,
): CurveLanding[] {
  const landings: CurveLanding[] = [];
  let prev = interpCurve(curve, ageWd);
  for (const anchor of WIN_RATE_CURVE_ANCHOR_DAYS) {
    if (anchor <= ageWd) continue;
    const c = interpCurve(curve, anchor);
    const dConv = c - prev;
    prev = c;
    if (dConv > 0) landings.push({ anchorWd: anchor, share: dConv });
  }
  return landings;
}

/**
 * A conversion override replaces the curve's remaining share for one quote.
 *
 * Bounded (0, 1] deliberately: zero conviction is a lost estimate, so a zero
 * gesture is routed to a status change rather than accepted as an override.
 * See the product brief, "Allowing 0% overrides — rejected".
 */
export const MIN_CONVERSION_OVERRIDE = 0.01;
export const MAX_CONVERSION_OVERRIDE = 1;

/** True when a proposed override is inside the permitted bounds. */
export function isValidConversionOverride(share: number): boolean {
  return (
    Number.isFinite(share) &&
    share >= MIN_CONVERSION_OVERRIDE &&
    share <= MAX_CONVERSION_OVERRIDE
  );
}

/**
 * Inverse of ``interpCurve``: the age at which cumulative C first reaches
 * ``target``, interpolated between anchors rather than snapped to the next one
 * so a hair's-width wobble does not flip a derived median between adjacent
 * anchors. Returns the last anchor if the curve never reaches target.
 *
 * Port of ``interp_age_at`` (bucket_calibration.py:60).
 */
export function interpAgeAt(
  curve: readonly CurvePoint[],
  target: number,
): number {
  if (curve.length === 0) return 0;
  if (target <= curve[0][1]) return curve[0][0];
  for (let i = 0; i < curve.length - 1; i++) {
    const [d0, c0] = curve[i];
    const [d1, c1] = curve[i + 1];
    if (c1 >= target) {
      if (c1 === c0) return d1;
      return d0 + ((target - c0) / (c1 - c0)) * (d1 - d0);
    }
  }
  return curve[curve.length - 1][0];
}

/**
 * The estimate→booking median in working days, derived from the bucket's own
 * win-rate curve: the age at which cumulative conversion first reaches half its
 * plateau.
 *
 * This is the answer to "how long does this kind of work usually take to come
 * back", measured from the bucket's real history and recalibrated nightly. It
 * is what makes an expected-conversion date derivable rather than something a
 * rep has to type: ``estimate_date + this many working days``.
 *
 * The curve is the single source of est→booking timing — there is no stored
 * scalar median. Port of ``eb_median_from_curve`` (bucket_calibration.py:78),
 * kept identical so the workspace's derived date and the projection's own
 * phasing cannot drift.
 */
export function ebMedianFromCurve(curve: readonly CurvePoint[]): number {
  if (curve.length === 0) return 0;
  const plateau = plateauOf(curve);
  if (plateau <= 0) return 0;
  return Math.round(interpAgeAt(curve, plateau / 2));
}
